import { useRef, useState } from "react";
import { fmt } from "../../format";
import { EmptyState } from "../EmptyState/EmptyState";
import { IconButton } from "../IconButton/IconButton";
import { Popover } from "../Popover/Popover";
import { notificationsLabel, unreadBadge, unreadCount } from "./notifications";
import type { NotificationItem } from "./topbarTypes";

const PANEL_MIN_WIDTH = 340;

/** The bell with its unread badge, and the panel with the feed. */
export function TopbarNotifications({ items }: { items: NotificationItem[] }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const unread = unreadCount(items);
  const close = (): void => setOpen(false);
  return (
    <span className="nb-notif-wrap">
      <IconButton
        ref={bellRef}
        icon="bell"
        label={notificationsLabel(unread)}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      />
      {unread > 0 ? (
        <span className="nb-notif-count" aria-hidden="true">
          {unreadBadge(unread)}
        </span>
      ) : null}
      <Popover anchorRef={bellRef} open={open} onClose={close} ariaLabel="Notifications" minWidth={PANEL_MIN_WIDTH} className="nb-notif-panel">
        <div className="nb-notif-head">
          <strong>Notifications</strong>
        </div>
        {items.length === 0 ? (
          <EmptyState icon="bell" title="No notifications" description="Updates about your account and your modules will show up here." />
        ) : (
          <NotificationList items={items} onSelected={close} />
        )}
      </Popover>
    </span>
  );
}

function NotificationList({ items, onSelected }: { items: NotificationItem[]; onSelected: () => void }): React.JSX.Element {
  return (
    <ul className="nb-notif-list">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`nb-notif-item${item.unread ? " is-unread" : ""}`}
            onClick={() => {
              onSelected();
              item.onSelect?.();
            }}
          >
            <strong>{item.title}</strong>
            {item.text ? <span>{item.text}</span> : null}
            {item.time ? <time dateTime={item.time}>{fmt.relative(item.time)}</time> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}
